import { create, StoreApi } from 'zustand';

// eslint-disable-next-line import/no-cycle
import { Trade } from '@/features/markets/api/get-trades';
import { AssetType, MarketStatus } from '@/types';

// eslint-disable-next-line import/no-cycle
import { Account } from './use-account-store';

export const COLLATERAL_SYMBOL = 'USD';
export const COLLATERAL_DECIMALS = 6n; // TODO: maybe env var
export const FUNDING_RATE_DECIMALS = 8n;
export const CUM_FUNDING_DECIMALS = 12n;

export interface MarketData {
  markPrice: bigint;
  indexPrice: bigint;
  oneHrFundingRate: bigint;
  priceChange: bigint;
  priceChangePct: bigint;
  cumFunding: bigint;
  volume?: bigint;
  quoteVolume?: bigint;
  openInterest?: bigint;
  updatedAtMs: number;
}

export interface MarketSpec {
  displayName: string;
  displaySymbol: string;
  base: string;
  quote: string;
  sizeDecimals: bigint;
  priceDecimals: bigint;
  initMarginRatio: bigint;
  fundedInitMarginRatio: bigint | null;
  maintMarginRatio: bigint;
  takerFee: bigint;
  isolated: boolean;
  status: MarketStatus;
  asset: AssetType;
  tickSizes: string[];
  defaultTickSize: string;
}

export interface OrderBook {
  bids: [bigint, bigint][]; // price, qty (sorted ascending)
  asks: [bigint, bigint][]; // price, qty (sorted descending)
}

export interface MarketStore {
  marketData: MarketData;
  marketSpec: MarketSpec;
  book: OrderBook;
  books: Record<string, OrderBook>;
  trades: Trade[];
  leverage: number | null;
  activeTickSize: string | null;
  setTrades: (trades: Trade[]) => void;
  setMarketData: (marketData: Partial<MarketData>) => void;
  setMarketSpec: (marketSpecs: Partial<MarketSpec>) => void;
  setBook: (book: OrderBook, tickSize?: string) => void;
  setActiveTickSize: (tickSize: string | null) => void;
}

const marketStores: Record<string, StoreApi<MarketStore>> = {};

const _createMarketStore = () =>
  create<MarketStore>((set) => ({
    marketData: {
      markPrice: 0n,
      indexPrice: 0n,
      oneHrFundingRate: 0n,
      priceChange: 0n,
      priceChangePct: 0n,
      cumFunding: 0n,
      updatedAtMs: 0,
    },
    marketSpec: {
      displayName: '',
      displaySymbol: '',
      base: '',
      quote: '',
      sizeDecimals: 0n,
      priceDecimals: 0n,
      initMarginRatio: 0n,
      fundedInitMarginRatio: null,
      maintMarginRatio: 0n,
      takerFee: 0n,
      isolated: false,
      status: MarketStatus.TRADING,
      asset: AssetType.STOCK,
      tickSizes: [],
      defaultTickSize: '',
    },
    leverage: null,
    activeTickSize: null,
    book: { bids: [], asks: [] },
    books: {},
    trades: [],
    setTrades: (trades) => set({ trades }),
    setMarketData: (marketData) =>
      set((state) => ({ marketData: { ...state.marketData, ...marketData } })),
    setMarketSpec: (marketSpec) =>
      set((state) => ({ marketSpec: { ...state.marketSpec, ...marketSpec } })),
    setBook: (book, tickSize) =>
      set((state) =>
        tickSize ? { books: { ...state.books, [tickSize]: book } } : { book },
      ),
    setActiveTickSize: (tickSize) => set({ activeTickSize: tickSize }),
  }));

export const getMarketStore = (symbol: string) => {
  if (!marketStores[symbol]) {
    marketStores[symbol] = _createMarketStore();
  }
  return marketStores[symbol];
};

export const createMarketStore = (symbol: string) => {
  if (!marketStores[symbol]) {
    marketStores[symbol] = _createMarketStore();
  }
};

export const getAllSymbols = (options?: { asset?: AssetType }) => {
  const symbols = Object.keys(marketStores);
  if (!options?.asset) {
    return symbols;
  }
  return symbols.filter(
    (key) => marketStores[key].getState().marketSpec.asset === options.asset,
  );
};

export const getAccountMarketStores = <T>(
  account: Account,
  marketStoresSlices: Record<string, T>,
) => {
  const accountMarkets = Object.keys(marketStoresSlices)
    .filter((symbol) => symbol in account.positions)
    .reduce(
      (acc, symbol) => {
        acc[symbol] = marketStoresSlices[symbol];
        return acc;
      },
      {} as Record<string, T>,
    );
  return accountMarkets;
};

export const setAllLeverages = (leverages: Record<string, number>) => {
  for (const [symbol, value] of Object.entries(leverages)) {
    if (marketStores[symbol]) {
      marketStores[symbol].setState({ leverage: value });
    }
  }
};

export const resetAllLeverages = () => {
  for (const symbol of Object.keys(marketStores)) {
    marketStores[symbol].setState({ leverage: null });
  }
};

export const getAllMarketStoreStates = () => {
  return Object.keys(marketStores).reduce(
    (acc, symbol) => {
      acc[symbol] = getMarketStore(symbol).getState();
      return acc;
    },
    {} as Record<string, MarketStore>,
  );
};
